/**
 * Phase 5 host-billing admin API hooks. See docs/domain/host-billing.md in the
 * API repo. Every figure is computed live from Keys / KeyHistory /
 * KeyHostAuthorizationChanges — there is no statement snapshot, so editing a
 * rate or a cycle setting re-computes past cycles too.
 *
 * The networking layer transforms request/response between camelCase (here) and
 * snake_case (API). Query-key param objects are the exception: they are joined
 * verbatim into the URL, so those keys must already be snake_case.
 */
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  QueryReturnType,
  mutatePostFn,
  mutatePutFn,
  mutateDeleteFn,
  client,
} from "../../services/networking.service";
import ENDPOINTS from "../../services/endPoints";

const commonQueryOpts = {
  retry: false as const,
  refetchOnWindowFocus: false,
  refetchOnMount: true,
  staleTime: 15 * 1000,
  cacheTime: 5 * 60 * 1000,
};

// ─── Queries ──────────────────────────────────────────────────────────

/** All hosts, one cycle. `cycleStart` is any unix-second instant inside the
 *  target cycle; each host's real window is derived from its own cadence. */
export const useHostBillingSummary = (
  cycleStart: number | null,
  enabled = true
): QueryReturnType<any> =>
  useQuery<never, never, any>(
    [ENDPOINTS.HOST_BILLING_SUMMARY, { cycle_start: cycleStart as number }],
    { ...commonQueryOpts, enabled: enabled && cycleStart != null }
  );

/** One host, one cycle — line items, exchanges, holding-not-billable list. */
export const useHostBillingStatement = (
  keyHostId: string | null,
  cycleStart: number | null,
  enabled = true
): QueryReturnType<any> =>
  useQuery<never, never, any>(
    [
      ENDPOINTS.HOST_BILLING_STATEMENT,
      { key_host_id: keyHostId as string, cycle_start: cycleStart as number },
    ],
    { ...commonQueryOpts, enabled: enabled && !!keyHostId && cycleStart != null }
  );

/** Derived [start,end) windows for a host's cadence (past `count` + current). */
export const useHostBillingCycles = (
  keyHostId: string | null,
  count = 12,
  enabled = true
): QueryReturnType<any> =>
  useQuery<never, never, any>(
    [ENDPOINTS.HOST_BILLING_CYCLES, { key_host_id: keyHostId as string, count }],
    { ...commonQueryOpts, enabled: enabled && !!keyHostId }
  );

export const useHostBillingSettings = (enabled = true): QueryReturnType<any> =>
  useQuery<never, never, any>([ENDPOINTS.HOST_BILLING_SETTINGS, {}], {
    ...commonQueryOpts,
    enabled,
  });

export const useHostBillingRates = (
  keyHostId: string | null,
  enabled = true
): QueryReturnType<any> =>
  useQuery<never, never, any>(
    [ENDPOINTS.HOST_BILLING_RATES, { key_host_id: keyHostId as string }],
    { ...commonQueryOpts, enabled: enabled && !!keyHostId }
  );

export const useHostBillingOverrides = (enabled = true): QueryReturnType<any> =>
  useQuery<never, never, any>([ENDPOINTS.HOST_BILLING_OVERRIDES, {}], {
    ...commonQueryOpts,
    enabled,
  });

// ─── Mutations ────────────────────────────────────────────────────────

export interface BillingSettingsPayload {
  cycleAnchor?: string;
  cycleFrequency?: "weekly" | "biweekly" | "monthly" | "quarterly";
  countedOperations?: string[];
  anchorDate?: number | null;
  timezone?: string;
  currency?: string;
}

export const useUpdateHostBillingSettings = () => {
  const qc = useQueryClient();
  return useMutation<any, any, BillingSettingsPayload>(
    (data) => mutatePutFn(ENDPOINTS.HOST_BILLING_SETTINGS, data),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_SETTINGS]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_SUMMARY]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_STATEMENT]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_CYCLES]);
      },
    }
  );
};

export interface RateCreatePayload {
  keyHostId: string;
  dimension: "key_days" | "exchanges";
  ratePence: number;
  effectiveFrom: number;
}

export const useCreateHostBillingRate = () => {
  const qc = useQueryClient();
  return useMutation<any, any, RateCreatePayload>(
    (data) => mutatePostFn(ENDPOINTS.HOST_BILLING_RATES, data),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_RATES]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_SUMMARY]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_STATEMENT]);
      },
    }
  );
};

export interface RateUpdatePayload {
  id: string;
  ratePence?: number;
  effectiveFrom?: number;
}

export const useUpdateHostBillingRate = () => {
  const qc = useQueryClient();
  return useMutation<any, any, RateUpdatePayload>(
    ({ id, ...data }) =>
      mutatePutFn(ENDPOINTS.HOST_BILLING_RATE.replace(":id", id), data),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_RATES]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_SUMMARY]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_STATEMENT]);
      },
    }
  );
};

export const useDeleteHostBillingRate = () => {
  const qc = useQueryClient();
  return useMutation<any, any, { id: string }>(
    ({ id }) =>
      mutateDeleteFn(ENDPOINTS.HOST_BILLING_RATE.replace(":id", id), {}),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_RATES]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_SUMMARY]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_STATEMENT]);
      },
    }
  );
};

export interface OverridePayload {
  keyHostId: string;
  cycleAnchor?: string | null;
  cycleFrequency?: "weekly" | "biweekly" | "monthly" | "quarterly" | null;
  anchorDate?: number | null;
  billingStartDate?: number | null;
  /** Which single metric forms this host's bill (only meaningful when rates
   *  exist for both dimensions). */
  billingMetric?: "key_days" | "exchanges" | null;
}

export const useUpsertHostBillingOverride = () => {
  const qc = useQueryClient();
  return useMutation<any, any, OverridePayload>(
    ({ keyHostId, ...data }) =>
      mutatePutFn(
        ENDPOINTS.HOST_BILLING_OVERRIDE.replace(":keyHostId", keyHostId),
        data
      ),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_OVERRIDES]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_SUMMARY]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_STATEMENT]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_CYCLES]);
      },
    }
  );
};

export const useDeleteHostBillingOverride = () => {
  const qc = useQueryClient();
  return useMutation<any, any, { keyHostId: string }>(
    ({ keyHostId }) =>
      mutateDeleteFn(
        ENDPOINTS.HOST_BILLING_OVERRIDE.replace(":keyHostId", keyHostId),
        {}
      ),
    {
      onSuccess: () => {
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_OVERRIDES]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_SUMMARY]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_STATEMENT]);
        qc.invalidateQueries([ENDPOINTS.HOST_BILLING_CYCLES]);
      },
    }
  );
};

// ─── CSV export (blob download, carries the auth header) ──────────────

async function downloadCsv(
  endpoint: string,
  params: Record<string, string | number>,
  filename: string
) {
  const res = await client.get(endpoint, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export const exportBillingSummaryCsv = (cycleStart: number) =>
  downloadCsv(
    ENDPOINTS.HOST_BILLING_SUMMARY_EXPORT,
    { cycleStart },
    `host-billing-summary-${cycleStart}.csv`
  );

export const exportBillingStatementCsv = (keyHostId: string, cycleStart: number) =>
  downloadCsv(
    ENDPOINTS.HOST_BILLING_STATEMENT_EXPORT,
    { keyHostId, cycleStart },
    `host-billing-statement-${keyHostId}-${cycleStart}.csv`
  );
